import { useRef, useState } from 'react'
import {
  useFloating,
  useClick,
  useFocus,
  useInteractions,
  useDismiss,
  useRole,
  FloatingPortal,
  FloatingArrow,
  arrow,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react'
import { ChevronRight } from 'lucide-react'
import { Z_INDEX } from 'styles/z-index'

type PopoverPlacement = 'top' | 'bottom' | 'left' | 'right'

interface FloatingPopoverProps {
  children: React.ReactElement
  title?: string
  description: string
  link?: {
    text?: string
    href?: string
  }
  placement?: PopoverPlacement
}

export function FloatingPopover({
  children,
  title,
  description,
  link,
  placement = 'bottom',
}: FloatingPopoverProps) {
  const [open, setOpen] = useState(false)
  const arrowRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(12),
      flip(),
      shift({ padding: 16 }),
      arrow({ element: arrowRef }),
    ],
  })

  const click = useClick(context)
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'dialog' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    focus,
    dismiss,
    role,
  ])

  // Destructure so we can wrap onClick — stops the click from bubbling
  // up to sortable table headers or other click-sensitive ancestors.
  const { onClick: referenceOnClick, ...referenceProps } =
    getReferenceProps() as React.HTMLAttributes<HTMLSpanElement>

  return (
    <>
      <span
        ref={refs.setReference}
        {...referenceProps}
        onClick={(e) => {
          e.stopPropagation()
          referenceOnClick?.(e)
        }}
        className="inline-flex"
      >
        {children}
      </span>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: Z_INDEX.TOOLTIP,
              filter: [
                'drop-shadow(0 1px 0px #e5e7eb)',
                'drop-shadow(0 -1px 0px #e5e7eb)',
                'drop-shadow(1px 0 0px #e5e7eb)',
                'drop-shadow(-1px 0 0px #e5e7eb)',
                'drop-shadow(0 2px 4px rgba(16,24,40,0.06))',
              ].join(' '),
            }}
            className="relative"
            {...getFloatingProps()}
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill="#ffffff"
              width={16}
              height={8}
            />
            <div
              className="bg-white rounded-xl overflow-hidden"
              style={{ width: 280 }}
            >
              {title && (
                <div className="px-3 py-3 border-b border-[#e5e7eb]">
                  <p className="text-[#1f2937] text-base font-semibold leading-6 tracking-[-0.2px]">
                    {title}
                  </p>
                </div>
              )}
              <div className="p-3 flex flex-col gap-3">
                <p className="text-[#6b7280] text-sm font-normal leading-5 tracking-[-0.2px]">
                  {description}
                </p>
                {link?.text && link?.href && (
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center text-[#0a9150] text-sm font-medium leading-5 tracking-[-0.2px]"
                  >
                    {link.text}
                    <ChevronRight size={16} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
